import { useState, useCallback, useEffect } from 'react';

export const useDeviceOrientation = () => {
  const [hasPermission, setHasPermission] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [showPermissionOverlay, setShowPermissionOverlay] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkDevice = () => {
      // Enhanced iOS detection using multiple methods
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      const maxTouchPoints = navigator.maxTouchPoints;
      
      // Check for iPad specifically (modern iPads report as MacIntel)
      const isIPadUserAgent = /iPad/.test(userAgent);
      const isIPadModern = platform === 'MacIntel' && maxTouchPoints > 1;
      
      // Check for iPhone/iPod
      const isIPhoneOrIPod = /iPhone|iPod/.test(userAgent);
      
      // Check for Chrome on iOS (reports as CriOS)
      const isChromeOnIOS = /CriOS/.test(userAgent);
      
      // Check for any iOS device
      const isIOSDevice = isIPadUserAgent || isIPadModern || isIPhoneOrIPod || isChromeOnIOS;
      
      // Android detection
      const isAndroidDevice = /Android/.test(userAgent);
      
      // Use global device info if available (set by HTML initialization script)
      const globalDeviceInfo = window.deviceInfo;
      let finalIsIOS = isIOSDevice;
      let finalIsAndroid = isAndroidDevice;
      
      if (globalDeviceInfo) {
        finalIsIOS = globalDeviceInfo.isIOS || globalDeviceInfo.isIPad;
        finalIsAndroid = globalDeviceInfo.isAndroid;
      }
      
      console.log('Enhanced device detection:', { 
        isIOSDevice: finalIsIOS, 
        isAndroidDevice: finalIsAndroid, 
        userAgent: userAgent.substring(0, 100),
        platform,
        maxTouchPoints,
        isIPadUserAgent,
        isIPadModern,
        isIPhoneOrIPod,
        isChromeOnIOS,
        globalDeviceInfo
      });
      
      setIsIOS(finalIsIOS);
      setIsAndroid(finalIsAndroid);

      // Check if we need to show the permission overlay
      const hasRequestedBefore = localStorage.getItem('hasRequestedMotionPermissions');
      console.log('Permission check:', { hasRequestedBefore, isIOSDevice: finalIsIOS, isAndroidDevice: finalIsAndroid });
      
      // Show overlay for mobile devices that haven't requested before
      if ((finalIsIOS || finalIsAndroid) && !hasRequestedBefore) {
        console.log('Showing permission overlay for mobile device');
        setShowPermissionOverlay(true);
      }
    };
    checkDevice();
  }, []);

  const requestPermission = useCallback(async () => {
    console.log('Requesting permission...', { isIOS, isAndroid });
    
    try {
      // For iOS Safari/Chrome - requires explicit permission
      if (isIOS && typeof DeviceOrientationEvent !== 'undefined' && 
          typeof DeviceOrientationEvent.requestPermission === 'function') {
        console.log('Requesting iOS permission...');
        
        try {
          const permission = await DeviceOrientationEvent.requestPermission();
          const isGranted = permission === 'granted';
          console.log('iOS permission result:', permission);
          setHasPermission(isGranted);
          
          if (isGranted) {
            localStorage.setItem('hasRequestedMotionPermissions', 'true');
            setShowPermissionOverlay(false);
            
            // Also request motion permission if available
            if (typeof DeviceMotionEvent !== 'undefined' && 
                typeof DeviceMotionEvent.requestPermission === 'function') {
              try {
                const motionPermission = await DeviceMotionEvent.requestPermission();
                console.log('iOS motion permission result:', motionPermission);
              } catch (motionError) {
                console.warn('Motion permission request failed:', motionError);
              }
            }
          } else {
            setError('Permission denied. Some features may not work properly.');
          }
          return isGranted;
        } catch (permissionError) {
          console.error('iOS permission request error:', permissionError);
          // Fall through to alternative handling
        }
      }
      
      // For Android and other devices, or iOS without permission API
      if (typeof DeviceOrientationEvent !== 'undefined') {
        console.log('Device orientation supported, granting permission');
        setHasPermission(true);
        localStorage.setItem('hasRequestedMotionPermissions', 'true');
        setShowPermissionOverlay(false);
        
        // Test device orientation on iOS devices that don't require explicit permission
        if (isIOS) {
          console.log('Testing iOS device orientation without explicit permission...');
          
          // Set up a test listener to see if orientation events work
          let orientationTestTimeout;
          let orientationWorking = false;
          
          const testOrientationListener = (event) => {
            if (event.alpha !== null || event.beta !== null || event.gamma !== null) {
              orientationWorking = true;
              console.log('iOS device orientation working:', {
                alpha: Math.round(event.alpha || 0),
                beta: Math.round(event.beta || 0),
                gamma: Math.round(event.gamma || 0)
              });
              window.removeEventListener('deviceorientation', testOrientationListener);
              clearTimeout(orientationTestTimeout);
            }
          };
          
          window.addEventListener('deviceorientation', testOrientationListener);
          
          // Test for 3 seconds
          orientationTestTimeout = setTimeout(() => {
            window.removeEventListener('deviceorientation', testOrientationListener);
            if (!orientationWorking) {
              console.warn('iOS device orientation not working - may need user interaction');
              setError('Device orientation may require user interaction to enable.');
            }
          }, 3000);
        }
        
        return true;
      }
      
      // Fallback for devices that don't support orientation events
      console.log('Device orientation not supported');
      setHasPermission(false);
      localStorage.setItem('hasRequestedMotionPermissions', 'true');
      setShowPermissionOverlay(false);
      setError('Device orientation not supported on this device.');
      return false;
    } catch (error) {
      console.error('Error requesting device orientation permission:', error);
      setError('Failed to request device orientation permission. Please try again.');
      return false;
    }
  }, [isIOS, isAndroid]);

  const skipPermission = useCallback(() => {
    console.log('Skipping permission request');
    localStorage.setItem('hasRequestedMotionPermissions', 'true');
    setShowPermissionOverlay(false);
  }, []);

  return {
    hasPermission,
    isIOS,
    isAndroid,
    showPermissionOverlay,
    requestPermission,
    skipPermission,
    error
  };
};
